// Country -> currency and country -> supported UI language mappings.
// Used to localize pricing and default language based on IP-detected country.
// Any country not listed falls back to USD / English.

export const CURRENCY_BY_COUNTRY = {
  // North America
  US: 'USD', CA: 'CAD', MX: 'MXN',
  // Eurozone
  DE: 'EUR', FR: 'EUR', ES: 'EUR', IT: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR',
  PT: 'EUR', IE: 'EUR', FI: 'EUR', GR: 'EUR', LU: 'EUR', SK: 'EUR', SI: 'EUR',
  EE: 'EUR', LV: 'EUR', LT: 'EUR', CY: 'EUR', MT: 'EUR', HR: 'EUR', MC: 'EUR',
  // Rest of Europe
  GB: 'GBP', CH: 'CHF', LI: 'CHF', SE: 'SEK', NO: 'NOK', DK: 'DKK', PL: 'PLN',
  CZ: 'CZK', HU: 'HUF', RO: 'RON', BG: 'BGN', IS: 'ISK', TR: 'TRY', RU: 'RUB',
  UA: 'UAH', RS: 'RSD',
  // Asia-Pacific
  JP: 'JPY', CN: 'CNY', HK: 'HKD', TW: 'TWD', KR: 'KRW', SG: 'SGD', IN: 'INR',
  ID: 'IDR', MY: 'MYR', TH: 'THB', VN: 'VND', PH: 'PHP', AU: 'AUD', NZ: 'NZD',
  PK: 'PKR', BD: 'BDT',
  // Middle East
  AE: 'AED', SA: 'SAR', QA: 'QAR', IL: 'ILS', KW: 'KWD', BH: 'BHD', OM: 'OMR',
  // Africa
  ZA: 'ZAR', NG: 'NGN', EG: 'EGP', KE: 'KES', GH: 'GHS', MA: 'MAD',
  // Latin America
  BR: 'BRL', AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN', VE: 'VES', EC: 'USD',
  GT: 'GTQ', CU: 'CUP', BO: 'BOB', DO: 'DOP', HN: 'HNL', PY: 'PYG', SV: 'USD',
  NI: 'NIO', CR: 'CRC', PA: 'PAB', UY: 'UYU', PR: 'USD',
};

export const LANGUAGE_BY_COUNTRY = {
  // Spanish
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es',
  EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es', HN: 'es', PY: 'es',
  SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es', GQ: 'es',
  // French
  FR: 'fr', BE: 'fr', LU: 'fr', MC: 'fr', CI: 'fr', SN: 'fr', CM: 'fr',
  HT: 'fr', CD: 'fr', CG: 'fr', ML: 'fr', NE: 'fr', BF: 'fr', TG: 'fr',
  BJ: 'fr', GA: 'fr',
  // German
  DE: 'de', AT: 'de', CH: 'de', LI: 'de',
  // Filipino
  PH: 'fil',
  // Japanese
  JP: 'ja',
  // Portuguese
  BR: 'pt', PT: 'pt', AO: 'pt', MZ: 'pt',
  // Italian
  IT: 'it', SM: 'it', VA: 'it',
  // Polish
  PL: 'pl',
  // Chinese
  CN: 'zh', TW: 'zh', HK: 'zh', MO: 'zh',
};

// Representative locale used purely for Intl.NumberFormat symbol/grouping rules.
export const LOCALE_BY_CURRENCY = {
  USD: 'en-US', CAD: 'en-CA', MXN: 'es-MX', EUR: 'de-DE', GBP: 'en-GB',
  CHF: 'de-CH', SEK: 'sv-SE', NOK: 'nb-NO', DKK: 'da-DK', PLN: 'pl-PL',
  CZK: 'cs-CZ', HUF: 'hu-HU', RON: 'ro-RO', BGN: 'bg-BG', ISK: 'is-IS',
  TRY: 'tr-TR', RUB: 'ru-RU', UAH: 'uk-UA', RSD: 'sr-RS', JPY: 'ja-JP',
  CNY: 'zh-CN', HKD: 'en-HK', TWD: 'zh-TW', KRW: 'ko-KR', SGD: 'en-SG',
  INR: 'en-IN', IDR: 'id-ID', MYR: 'ms-MY', THB: 'th-TH', VND: 'vi-VN',
  PHP: 'en-PH', AUD: 'en-AU', NZD: 'en-NZ', PKR: 'en-PK', BDT: 'bn-BD',
  AED: 'ar-AE', SAR: 'ar-SA', QAR: 'ar-QA', ILS: 'he-IL', KWD: 'ar-KW',
  BHD: 'ar-BH', OMR: 'ar-OM', ZAR: 'en-ZA', NGN: 'en-NG', EGP: 'ar-EG',
  KES: 'en-KE', GHS: 'en-GH', MAD: 'ar-MA', BRL: 'pt-BR', ARS: 'es-AR',
  CLP: 'es-CL', COP: 'es-CO', PEN: 'es-PE', VES: 'es-VE', GTQ: 'es-GT',
  CUP: 'es-CU', BOB: 'es-BO', DOP: 'es-DO', HNL: 'es-HN', PYG: 'es-PY',
  NIO: 'es-NI', CRC: 'es-CR', PAB: 'es-PA', UYU: 'es-UY',
};

export const DEFAULT_CURRENCY = 'USD';
export const DEFAULT_LOCALE = 'en-US';
export const DEFAULT_LANGUAGE = 'en';

export function getCurrencyForCountry(countryCode) {
  if (!countryCode) return DEFAULT_CURRENCY;
  return CURRENCY_BY_COUNTRY[countryCode.toUpperCase()] || DEFAULT_CURRENCY;
}

export function getLanguageForCountry(countryCode) {
  if (!countryCode) return null;
  return LANGUAGE_BY_COUNTRY[countryCode.toUpperCase()] || null;
}

export function getLocaleForCurrency(currencyCode) {
  return LOCALE_BY_CURRENCY[currencyCode] || DEFAULT_LOCALE;
}
